import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calculator, ArrowLeft, TrendingUp } from 'lucide-react'
import { config } from '@workspace/public-utils'
import { Seo } from '../../components/Seo'

interface SumData {
  sum: number
  frequency: number
  percentage: number
}

const NumberSumAnalysisPage = () => {
  const [selectedRange, setSelectedRange] = useState(0) // 0 = 10, 1 = 30, 2 = 50
  const [analysisData, setAnalysisData] = useState<Record<number, any>>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ranges = [10, 30, 50]
  const scopeMap = ['recent_10', 'recent_30', 'recent_50']

  useEffect(() => {
    fetchAnalysis(ranges[selectedRange])
  }, [selectedRange])

  const fetchAnalysis = async (range: number) => {
    try {
      setLoading(true)
      setError(null)

      const scope = scopeMap[selectedRange]
      const response = await fetch(
        `${config.API_BASE_URL}/api/v1/public/analysis/number-sum?scope=${scope}`
      )

      if (!response.ok) {
        throw new Error('분석 데이터를 불러오는데 실패했습니다.')
      }

      const data = await response.json()
      
      if (!data.sum_data || data.sum_data.length === 0) {
        throw new Error('분석할 데이터가 없습니다. 로또 결과가 충분히 쌓이면 분석이 표시됩니다.')
      }

      // Convert sumData to SumData interface
      const sumData: SumData[] = data.sum_data.map((item: any) => ({
        sum: item.sum,
        frequency: item.frequency,
        percentage: item.percentage
      }))

      setAnalysisData((prev) => ({
        ...prev,
        [range]: {
          sumData,
          total_analyzed: data.total_analyzed,
          winning_range: data.winning_range,
          average_sum: data.average_sum,
          min_sum: data.min_sum,
          max_sum: data.max_sum
        }
      }))
    } catch (err) {
      console.error('Error fetching analysis:', err)
      setError(err instanceof Error ? err.message : '데이터를 불러올 수 없습니다.')
    } finally {
      setLoading(false)
    }
  }

  const currentData = analysisData[ranges[selectedRange]]

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="숫자 총합 분석"
        description="역대 당첨번호 총합 분포를 확인하고, 자주 등장하는 총합 구간(위닝 레인지)을 참고하세요."
        canonicalPath="/analysis/number-sum"
      />
      {/* Header */}
      <section className="bg-gradient-to-br from-blue-500 via-cyan-500 to-blue-600 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <Link to="/analysis" className="inline-flex items-center text-white hover:text-white/80 mb-6 transition-colors">
            <ArrowLeft className="w-5 h-5 mr-2" />
            분석 방법으로 돌아가기
          </Link>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center mb-4">
              <Calculator className="w-12 h-12 text-white mr-4" />
              <h1 className="text-4xl md:text-6xl font-black text-white">
                숫자 총합 분석
              </h1>
            </div>
            <p className="text-xl text-white/90 text-center max-w-3xl mx-auto">
              역대 당첨 번호 총합의 분포를 분석하여 위닝 레인지를 찾아보세요
            </p>
          </motion.div>
        </div>
      </section>

      {/* Range Selector */}
      <section className="py-8 px-4 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <h2 className="text-2xl font-bold text-gray-900">분석 기간 선택</h2>
            <div className="flex gap-2">
              {ranges.map((range, index) => (
                <button
                  key={range}
                  onClick={() => setSelectedRange(index)}
                  className={`px-6 py-3 rounded-lg font-bold transition-all duration-300 ${
                    selectedRange === index
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg scale-105'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  최근 {range}회
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
            <p className="text-red-700 font-medium">{error}</p>
          </div>
        </div>
      )}

      {/* Analysis Content */}
      {!loading && !error && currentData && (
        <div className="max-w-7xl mx-auto px-4 py-12">
          {/* Statistics Summary */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12"
          >
            <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className="text-sm text-gray-600 mb-2">평균 총합</div>
              <div className="text-4xl font-black text-blue-600">
                {currentData.average_sum.toFixed(0)}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className="text-sm text-gray-600 mb-2">최소 총합</div>
              <div className="text-4xl font-black text-gray-700">
                {currentData.min_sum}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className="text-sm text-gray-600 mb-2">최대 총합</div>
              <div className="text-4xl font-black text-gray-700">
                {currentData.max_sum}
              </div>
            </div>
            <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
              <div className="text-sm text-gray-600 mb-2">분석 회차</div>
              <div className="text-4xl font-black text-cyan-600">
                {currentData.total_analyzed}
              </div>
            </div>
          </motion.div>

          {/* Winning Range */}
          {currentData.winning_range && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-12 bg-gradient-to-r from-green-400 to-emerald-500 rounded-2xl p-8 text-center shadow-2xl"
            >
              <div className="flex items-center justify-center mb-4">
                <TrendingUp className="w-12 h-12 text-white mr-3" />
                <h2 className="text-3xl font-black text-white">위닝 레인지</h2>
              </div>
              <div className="text-6xl font-black text-white mb-2">
                {currentData.winning_range.sum}
              </div>
              <div className="text-xl text-white/90">
                {currentData.winning_range.frequency}회 출현 ({currentData.winning_range.percentage.toFixed(1)}%)
              </div>
            </motion.div>
          )}

          {/* Distribution Chart */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="bg-white rounded-2xl p-8 shadow-lg mb-12"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              총합 분포
            </h3>
            <div className="space-y-3">
              {currentData.sumData.map((data: SumData) => (
                <div key={data.sum} className="flex items-center gap-4">
                  <div className="w-24 text-right font-bold text-gray-700">
                    총합 {data.sum}
                  </div>
                  <div className="flex-1 bg-gray-200 rounded-full h-8 overflow-hidden">
                    <div
                      className={`h-full flex items-center justify-end pr-3 text-white font-bold text-sm transition-all duration-500 ${
                        data.sum === currentData.winning_range.sum
                          ? 'bg-gradient-to-r from-green-400 to-emerald-500'
                          : 'bg-gradient-to-r from-blue-500 to-cyan-500'
                      }`}
                      style={{ width: `${data.percentage}%` }}
                    >
                      {data.percentage > 5 && `${data.percentage.toFixed(1)}%`}
                    </div>
                  </div>
                  <div className="w-20 text-left text-gray-600">
                    {data.frequency}회
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Sum Ranges */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
          >
            <div className="bg-blue-50 rounded-2xl p-6 text-center">
              <h4 className="text-lg font-bold text-blue-700 mb-2">낮은 범위</h4>
              <div className="text-3xl font-black text-blue-900 mb-2">
                {currentData.min_sum} - {Math.round(currentData.average_sum - 20)}
              </div>
              <p className="text-sm text-blue-600">평균보다 낮은 총합</p>
            </div>
            <div className="bg-green-50 rounded-2xl p-6 text-center border-2 border-green-400">
              <h4 className="text-lg font-bold text-green-700 mb-2">중간 범위 (추천)</h4>
              <div className="text-3xl font-black text-green-900 mb-2">
                {Math.round(currentData.average_sum - 20)} - {Math.round(currentData.average_sum + 20)}
              </div>
              <p className="text-sm text-green-600">가장 빈번한 총합 범위</p>
            </div>
            <div className="bg-purple-50 rounded-2xl p-6 text-center">
              <h4 className="text-lg font-bold text-purple-700 mb-2">높은 범위</h4>
              <div className="text-3xl font-black text-purple-900 mb-2">
                {Math.round(currentData.average_sum + 20)} - {currentData.max_sum}
              </div>
              <p className="text-sm text-purple-600">평균보다 높은 총합</p>
            </div>
          </motion.div>

          {/* Disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-lg"
          >
            <p className="text-gray-700">
              <strong className="text-yellow-700">⚠️ 안내:</strong> 이 분석은 과거 당첨 번호의 통계적 분석이며, 
              미래 당첨 번호를 예측하거나 보장하지 않습니다. 
              로또는 무작위 추첨이므로 모든 번호는 동일한 당첨 확률을 가집니다.
            </p>
          </motion.div>
        </div>
      )}
    </div>
  )
}

export default NumberSumAnalysisPage

